import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, UploadSimple, X, FileText, CircleNotch, Robot } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import CityAutocomplete from "@/components/CityAutocomplete";

const LABELS = {
  address: "Indirizzo immobile",
  city: "Città",
  province: "Provincia",
  zip_code: "CAP",
  owner_first_name: "Nome proprietario",
  owner_last_name: "Cognome proprietario",
  owner_company_name: "Ragione sociale",
  owner_vat_number: "P.IVA",
  owner_fiscal_code: "Codice fiscale",
  cadastral_data: "Dati catastali",
};
const fmtSize = (b) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`);

function Badge({ kind }) {
  const map = {
    ok: ["bg-emerald-100 text-emerald-800", "✓ Confermato"],
    conflict: ["bg-orange-100 text-orange-800", "⚠️ Verifica richiesta"],
    missing: ["bg-red-100 text-red-700", "Dato mancante"],
    empty: ["bg-slate-100 text-slate-500", "Non trovato"],
  };
  const [cls, txt] = map[kind];
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
}

function FieldReview({ field, required, extracted, options, value, onChange, onPick, label }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(null);
  const autoFilled = field === "zip_code" && !!value && !extracted;
  const kind = options ? "conflict" : (extracted || autoFilled) && value ? "ok" : required ? "missing" : "empty";
  const textInput = (props) => (field === "city"
    ? <CityAutocomplete value={value} onChange={onChange} onPick={onPick} placeholder={props.placeholder || "Milano"} testid={`ai-field-${field}-input`} />
    : <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`ai-field-${field}-input`} {...props} />);
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid={`ai-field-${field}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-700">{label || LABELS[field]}{required ? " *" : ""}</span>
        <Badge kind={kind} />
      </div>
      {field === "zip_code" ? (
        <Input value={value} readOnly disabled placeholder="Seleziona la città dall'elenco" data-testid="ai-field-zip_code-input" />
      ) : options ? (
        <div className="space-y-1.5">
          {[...options.map((o, i) => ({ key: i, label: o })), { key: "manual", label: "Inserisci manualmente" }].map((o) => (
            <label key={o.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={`ai-${field}`}
                checked={pick === o.key}
                onChange={() => { setPick(o.key); onChange(o.key === "manual" ? "" : o.label, o.key !== "manual"); }}
                data-testid={`ai-field-${field}-opt-${o.key}`}
              />
              <span>{o.label}</span>
            </label>
          ))}
          {pick === "manual" && (
            textInput({ autoFocus: true })
          )}
        </div>
      ) : kind === "ok" && !editing ? (
        <button type="button" onClick={() => setEditing(true)} className="w-full text-left text-sm text-slate-900 hover:text-indigo-700" title="Clicca per modificare" data-testid={`ai-field-${field}-value`}>
          {value || "—"}
        </button>
      ) : (
        textInput({ placeholder: "Compila manualmente" })
      )}
    </div>
  );
}

// Iter209es — Creazione immobile assistita da AI: upload documenti → estrazione → revisione.
// Iter209ex — Salva nell'archivio del nuovo immobile i documenti usati per l'analisi AI.
export async function attachAnalyzedDocs(propertyId, files) {
  if (!propertyId || !files?.length) return 0;
  const results = await Promise.allSettled(files.map((f) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", "generic");
    return api.post(`/properties/${propertyId}/documents`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 10 * 60 * 1000,
    });
  }));
  const ok = results.filter((r) => r.status === "fulfilled").length;
  if (ok) toast.success(ok === 1 ? "Documento analizzato salvato nell'archivio" : `${ok} documenti analizzati salvati nell'archivio`);
  if (ok < files.length) toast.error(`${files.length - ok} documenti non salvati: caricali dall'archivio dell'immobile`);
  return ok;
}

export default function AiPropertyCreate({ onConfirm, onCancel, submitting }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [cityPicked, setCityPicked] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [values, setValues] = useState({});
  const [ownerType, setOwnerType] = useState("privato");

  const addFiles = (list) => setFiles((prev) => [...prev, ...Array.from(list || [])].slice(0, 5));
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const pollRef = useRef(null);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const applyResult = (data) => {
    const f = data?.fields || {};
    const c = data?.conflicts || {};
    setResult(data);
    setValues(f);
    setOwnerType(f.owner_company_name || f.owner_vat_number || c.owner_company_name || c.owner_vat_number ? "azienda" : "privato");
    (data?.warnings || []).forEach((w) => toast.warning(w));
  };

  const poll = (jobId) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/properties/ai-extract/${jobId}`);
        if (data.status === "done") { applyResult(data.result); setAnalyzing(false); return; }
        if (data.status === "error") { toast.error(data.error || "Analisi non riuscita"); setAnalyzing(false); return; }
        poll(jobId);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Analisi non riuscita");
        setAnalyzing(false);
      }
    }, 3000);
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/properties/ai-extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      poll(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analisi non riuscita");
      setAnalyzing(false);
    }
  };

  if (analyzing) {
    return (
      <div className="py-10 text-center space-y-3" data-testid="ai-analyzing">
        <CircleNotch size={32} className="mx-auto animate-spin text-indigo-600" />
        <p className="text-sm text-slate-600">Analisi in corso...</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="space-y-3" data-testid="ai-upload-step">
        <p className="text-sm text-muted-foreground">
          Carica i documenti dell'immobile: <strong>documento d'identità</strong>, <strong>atto di acquisto</strong>, <strong>visura catastale</strong> (PDF, DOCX o immagini, max 5).
        </p>
        <div
          className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragActive ? "border-purple-500 bg-purple-50/60" : "border-slate-300 bg-slate-50/50"}`}
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          data-testid="ai-dropzone"
        >
          <UploadSimple size={28} className="mx-auto text-slate-400" />
          <p className="text-sm text-slate-600 mt-2">Trascina qui i documenti</p>
          <p className="text-xs text-slate-400 mb-3">oppure</p>
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid="ai-pick">
            <Paperclip size={14} className="mr-1.5" /> Scegli file dal PC
          </Button>
          <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.doc,.txt,image/*" className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="ai-input" />
        </div>
        {files.length > 0 && (
          <ul className="divide-y rounded-lg border" data-testid="ai-files-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                <FileText size={16} className="text-slate-400 shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-xs text-slate-500">{fmtSize(f.size)}</span>
                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label={`Rimuovi ${f.name}`} data-testid={`ai-file-remove-${i}`}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button className="w-full ht-gradient border-0" disabled={!files.length} onClick={analyze} data-testid="ai-analyze-btn">
          <Robot size={16} className="mr-2" /> Analizza documenti
        </Button>
        <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground" data-testid="ai-cancel">← Indietro</button>
      </div>
    );
  }

  const conflicts = result.conflicts || {};
  const extracted = result.fields || {};
  const ownerFields = ownerType === "azienda" ? ["owner_company_name", "owner_vat_number"] : ["owner_first_name", "owner_last_name"];
  const shown = ["address", "city", "province", "zip_code", ...ownerFields, "owner_fiscal_code", "cadastral_data"];
  const required = new Set(["address", "city", "province", ...ownerFields]);
  const ready = [...required].every((k) => (values[k] || "").trim());

  const confirm = () => {
    const v = Object.fromEntries(Object.entries(values).map(([k, x]) => [k, (x || "").trim()]));
    const isAz = ownerType === "azienda";
    onConfirm({
      owner_type: ownerType,
      address: v.address, city: v.city, province: (v.province || "").toUpperCase().slice(0, 2), zip_code: v.zip_code || "",
      owner_first_name: isAz ? "" : v.owner_first_name, owner_last_name: isAz ? "" : v.owner_last_name,
      owner_company_name: isAz ? v.owner_company_name : "", owner_vat_number: isAz ? v.owner_vat_number : "",
      owner_company_fiscal_code: isAz ? (v.owner_fiscal_code || "") : "",
      cadastral_data: v.cadastral_data || "",
      notes: !isAz && v.owner_fiscal_code ? `Codice fiscale proprietario: ${v.owner_fiscal_code}` : "",
    }, files);
  };

  return (
    <div className="space-y-3" data-testid="ai-review-step">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-600">Proprietario:</span>
        {["privato", "azienda"].map((t) => (
          <button key={t} type="button" onClick={() => setOwnerType(t)}
            className={`px-3 py-1 rounded-full border text-xs font-semibold ${ownerType === t ? "border-indigo-500 bg-indigo-50 text-indigo-800" : "border-slate-200 text-slate-600"}`}
            data-testid={`ai-owner-type-${t}`}>
            {t === "privato" ? "Privato" : "P.IVA"}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {shown.map((k) => (
          <FieldReview key={`${k}-${ownerType}`} field={k} required={required.has(k)} extracted={!!extracted[k]}
            options={conflicts[k]} value={values[k] || ""}
            label={k === "zip_code" && cityPicked ? "CAP (auto)" : undefined}
            onChange={(x, fromOption) => {
              if (k === "city" && !fromOption) { setCityPicked(false); setValues((s) => ({ ...s, city: x, province: "", zip_code: "" })); }
              else setValues((s) => ({ ...s, [k]: x }));
            }}
            onPick={(c) => { setCityPicked(true); setValues((s) => ({ ...s, city: c.name, province: c.province, zip_code: c.cap })); }} />
        ))}
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="ai-attach-note">
        <Paperclip size={12} /> {files.length === 1 ? "Il documento analizzato verrà salvato" : `I ${files.length} documenti analizzati verranno salvati`} nell'archivio dell'immobile.
      </p>
      <Button className="w-full ht-gradient border-0" disabled={!ready || submitting} onClick={confirm} data-testid="ai-confirm-btn">
        {submitting ? "Creazione…" : "Conferma e continua"}
      </Button>
      <button type="button" onClick={() => setResult(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="ai-reupload">← Carica altri documenti</button>
    </div>
  );
}
